import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';

import { ICD_ERROR } from 'src/services/ICD/icd-client';
import { obtenerDetalle } from 'src/services/ICD/icd-search-service';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------
// Panel de detalle de una entidad CIE-11. Se usa en dos sitios con la misma
// carga: expandido dentro de la fila del desplegable (`compacto`) y aireado en
// la columna derecha del diálogo avanzado.
//
// Cada bloque se omite si viene vacío: la mayoría de entidades no traen
// definición, y un hueco reservado se lee como un fallo de carga.
// ----------------------------------------------------------------------

const MAX_SINONIMOS = 6;

const FUENTE_MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

// El tono legible de un color depende del modo: `darker` desaparece en oscuro.
const tonoLegible = (theme, color) =>
  theme.palette[color][theme.palette.mode === 'light' ? 'darker' : 'lighter'];

// ----------------------------------------------------------------------

function Seccion({ titulo, compacto, children }) {
  return (
    <Stack spacing={compacto ? 0.5 : 1} sx={{ minWidth: 0 }}>
      <Typography
        variant="overline"
        sx={{ color: 'text.disabled', lineHeight: 1.4, fontSize: compacto ? 10 : 11 }}
      >
        {titulo}
      </Typography>
      {children}
    </Stack>
  );
}

Seccion.propTypes = {
  compacto: PropTypes.bool,
  titulo: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// ----------------------------------------------------------------------

function CargandoDetalle({ compacto }) {
  return (
    <Stack spacing={compacto ? 0.75 : 1.25} sx={{ width: 1 }}>
      <Skeleton variant="text" width="30%" height={compacto ? 12 : 16} />
      <Skeleton variant="text" width="100%" height={compacto ? 14 : 18} />
      <Skeleton variant="text" width="80%" height={compacto ? 14 : 18} />
      <Skeleton variant="rounded" width="60%" height={compacto ? 20 : 26} />
      <Skeleton variant="text" width="70%" height={compacto ? 12 : 16} />
    </Stack>
  );
}

CargandoDetalle.propTypes = {
  compacto: PropTypes.bool,
};

// ----------------------------------------------------------------------

function Exclusiones({ exclusiones, compacto }) {
  return (
    <Stack
      spacing={compacto ? 0.5 : 0.75}
      sx={{
        borderRadius: 1,
        p: compacto ? 0.75 : 1.25,
        bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
      }}
    >
      {exclusiones.map((exclusion, indice) => (
        <Stack
          key={exclusion.id ?? `${exclusion.etiqueta}-${indice}`}
          spacing={0.75}
          direction="row"
          alignItems="flex-start"
        >
          <Iconify
            width={compacto ? 14 : 16}
            icon="solar:forbidden-circle-bold"
            sx={{ mt: '2px', flexShrink: 0, color: 'warning.main' }}
          />

          <Typography
            variant={compacto ? 'caption' : 'body2'}
            sx={{ minWidth: 0, color: (theme) => tonoLegible(theme, 'warning') }}
          >
            {exclusion.etiqueta}
            {exclusion.codigo ? (
              <Box component="span" sx={{ ml: 0.75, opacity: 0.8, fontFamily: FUENTE_MONO }}>
                {exclusion.codigo}
              </Box>
            ) : null}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

Exclusiones.propTypes = {
  compacto: PropTypes.bool,
  exclusiones: PropTypes.array.isRequired,
};

// ----------------------------------------------------------------------

function RutaJerarquica({ ruta, onNavegar }) {
  return (
    <Breadcrumbs
      separator={
        <Iconify width={14} icon="eva:arrow-ios-forward-fill" sx={{ color: 'text.disabled' }} />
      }
      sx={{
        '& .MuiBreadcrumbs-ol': { rowGap: 0.25 },
        '& .MuiBreadcrumbs-separator': { mx: 0.5 },
      }}
    >
      {ruta.map((nodo, indice) => {
        const esActual = indice === ruta.length - 1;
        const etiqueta = nodo.codigo ? `${nodo.codigo} · ${nodo.titulo}` : nodo.titulo;

        if (esActual || !onNavegar) {
          return (
            <Typography
              key={nodo.id}
              variant="caption"
              sx={{
                fontWeight: esActual ? 600 : 400,
                color: esActual ? 'text.primary' : 'text.secondary',
              }}
            >
              {etiqueta}
            </Typography>
          );
        }

        return (
          <Link
            key={nodo.id}
            type="button"
            variant="caption"
            component="button"
            underline="hover"
            onClick={() => onNavegar(nodo.id)}
            sx={{ p: 0, border: 0, bgcolor: 'transparent', color: 'text.secondary' }}
          >
            {etiqueta}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
}

RutaJerarquica.propTypes = {
  onNavegar: PropTypes.func,
  ruta: PropTypes.array.isRequired,
};

// ----------------------------------------------------------------------

export default function IcdEntityDetail({ id, compacto = false, onNavegar }) {
  const [detalle, setDetalle] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [reintento, setReintento] = useState(null);
  const [verTodosSinonimos, setVerTodosSinonimos] = useState(false);

  useEffect(() => {
    if (!id) {
      setDetalle(null);
      setError(null);
      setCargando(false);
      return undefined;
    }

    const control = new AbortController();

    setDetalle(null);
    setError(null);
    setCargando(true);
    setVerTodosSinonimos(false);

    // Reintentar salta la caché: un detalle parcial por un fallo transitorio
    // volvería a leerse igual de roto.
    obtenerDetalle(id, { signal: control.signal, forzar: reintento?.id === id })
      .then((datos) => {
        // Un detalle ya cacheado resuelve sin mirar el signal: sin esta guarda,
        // cambiar de entidad rápido pinta el anterior sobre el actual.
        if (control.signal.aborted) return;
        setDetalle(datos);
        setCargando(false);
      })
      .catch((fallo) => {
        // Cancelar es lo normal al cambiar de entidad: no es un error que pintar.
        if (fallo?.tipo === ICD_ERROR.CANCELADO) return;
        setError(fallo);
        setCargando(false);
      });

    return () => control.abort();
  }, [id, reintento]);

  // El reintento se marca POR id: si no, cambiar de entidad después de un fallo
  // dejaría el panel saltándose la caché el resto de la sesión.
  const reintentar = useCallback(
    () => setReintento((previo) => ({ id, n: (previo?.id === id ? previo.n : 0) + 1 })),
    [id]
  );

  if (!id) return null;

  const contenedorSx = {
    minWidth: 0,
    ...(compacto && {
      p: 1,
      maxHeight: 220,
      overflowY: 'auto',
      '&::-webkit-scrollbar': { width: 6 },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: 3,
        backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.32),
      },
    }),
  };

  if (cargando) {
    return (
      <Box sx={contenedorSx}>
        <CargandoDetalle compacto={compacto} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={contenedorSx}>
        <Alert
          severity="error"
          action={
            <Button color="error" size="small" onClick={reintentar}>
              Reintentar
            </Button>
          }
          sx={{ alignItems: 'center', ...(compacto && { py: 0.25 }) }}
        >
          <Typography variant={compacto ? 'caption' : 'body2'}>{error.message}</Typography>
        </Alert>
      </Box>
    );
  }

  if (!detalle) return null;

  const { definicion, sinonimos, exclusiones, ruta, urlNavegador } = detalle;

  const sinonimosVisibles = verTodosSinonimos ? sinonimos : sinonimos.slice(0, MAX_SINONIMOS);
  const sinonimosOcultos = sinonimos.length - sinonimosVisibles.length;
  const tieneRuta = Array.isArray(ruta) && ruta.length > 0;

  return (
    <Stack spacing={compacto ? 1.25 : 2} sx={contenedorSx}>
      {definicion ? (
        <Typography
          variant={compacto ? 'caption' : 'body2'}
          sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}
        >
          {definicion}
        </Typography>
      ) : null}

      {sinonimos.length > 0 ? (
        <Seccion titulo="Sinónimos" compacto={compacto}>
          <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5 }}>
            {sinonimosVisibles.map((sinonimo) => (
              <Chip key={sinonimo} size="small" label={sinonimo} variant="soft" />
            ))}

            {sinonimosOcultos > 0 ? (
              <Chip
                size="small"
                color="primary"
                variant="soft"
                label={`+${sinonimosOcultos} más`}
                onClick={() => setVerTodosSinonimos(true)}
              />
            ) : null}

            {verTodosSinonimos && sinonimos.length > MAX_SINONIMOS ? (
              <Chip
                size="small"
                variant="soft"
                label="Ver menos"
                onClick={() => setVerTodosSinonimos(false)}
              />
            ) : null}
          </Stack>
        </Seccion>
      ) : null}

      {exclusiones.length > 0 ? (
        <Seccion titulo="No incluye" compacto={compacto}>
          <Exclusiones exclusiones={exclusiones} compacto={compacto} />
        </Seccion>
      ) : null}

      {tieneRuta ? (
        <Seccion titulo="Clasificación" compacto={compacto}>
          {/* En modo compacto la ruta es informativa: navegar cerraría el desplegable. */}
          <RutaJerarquica ruta={ruta} onNavegar={compacto ? undefined : onNavegar} />
        </Seccion>
      ) : null}

      {urlNavegador ? (
        <Link
          variant="caption"
          target="_blank"
          href={urlNavegador}
          underline="hover"
          rel="noopener noreferrer"
          sx={{ gap: 0.5, alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center' }}
        >
          <Iconify width={14} icon="eva:external-link-fill" />
          Ver en el navegador de la OMS
        </Link>
      ) : null}
    </Stack>
  );
}

IcdEntityDetail.propTypes = {
  compacto: PropTypes.bool,
  onNavegar: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
